<template>
  <DialogFull :data="dialogSettings" @close="close">
    <template v-slot:dialog.body>
      <ValidationObserver ref="basicDetailsForm">
        <div class="container col-lg-10 col-sm-12">
          <Permissioned section="bookings" :id="booking.id">
            <ValidationProvider
              v-slot="{ errors }"
              class="full-width"
              rules="required|max:250"
              permission="edit.role-title"
            >
              <v-text-field
                :error-messages="getErrors(errors)"
                label="Role Title"
                v-model="title"
              />
            </ValidationProvider>
          </Permissioned>
          <v-row>
            <v-col class="col-lg-6 col-12">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <Select
                  autocomplete
                  v-model="contractType"
                  height="50"
                  class="mt-2"
                  label="Contract Type"
                  placeholder="Select Contract Type"
                  :items="contractTypes"
                  :loading="isFetchingContractTypes"
                  item-text="originalName"
                  item-value="originalName"
                  :error-messages="getErrors(errors)"
                />
              </ValidationProvider>
            </v-col>
            <v-col class="col-lg-6 col-12 ">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <Select
                  autocomplete
                  v-model="workingHour"
                  height="50"
                  class="mt-2"
                  label="Working Hours"
                  placeholder="Select Working Hours"
                  :items="workingHours"
                  :loading="isFetchingWorkingHours"
                  item-text="name"
                  item-value="name"
                  :error-messages="getErrors(errors)"
                />
              </ValidationProvider>
            </v-col>
          </v-row>
          <Permissioned section="bookings" :id="booking.id" class="flex-column">
            <Select
              autocomplete
              v-model="location"
              height="50"
              class="mt-0"
              label="Location"
              placeholder="Select location"
              item-text="label"
              item-value="value"
              :items="formattedLocations"
              :loading="isFetchingData"
              permission="edit.location"
            />
            <Select
              autocomplete
              v-model="grade"
              item-text="name"
              item-value="name"
              height="50"
              class="mt-0"
              label="Grade"
              placeholder="Select grade"
              :items="grades"
              :loading="isFetchingGrades"
              permission="edit.grade"
            />
            <div class="d-flex align-baseline flex-wrap" v-if="editSalary">
              <v-text-field
                v-money="moneyConfig"
                label="Salary"
                prefix="£"
                v-model="salaryBandLower"
                :value="salaryBandLower"
                :readonly="!editSalaryBandLower"
                aria-label="Salary band lower"
              />
              <label class="mx-2">to</label>
              <v-text-field
                aria-label="Salary band upper"
                v-money="moneyConfig"
                prefix="£"
                v-model="salaryBandUpper"
                :value="salaryBandUpper"
                :readonly="!editSalaryBandUpper"
              />
            </div>
            <Select
              :disabled="!editComplianceRequirements"
              v-model="complianceRequirements"
              :items="complianceRequirementsOptions"
              :loading="isFetchingComplianceRequirements"
              label="Compliance Requirements"
              multiple
            >
              <template v-slot:selection="{ data }">
                <Chip>
                  {{ data.item }}
                </Chip>
              </template>
            </Select>
            <Select
              :disabled="!editOtherRequirements"
              v-model="otherRequirements"
              :items="otherRequirementsOptions"
              :loading="isFetchingOtherRequirements"
              label="Other Requirements"
              multiple
            >
              <template v-slot:selection="{ data }">
                <Chip>
                  {{ data.item }}
                </Chip>
              </template>
            </Select>
            <div permission="edit.external-description">
              <div class="mb-2 mt-3">
                {{ `${isTemporaryJob ? "" : "External"} Description` }}
              </div>
              <quill-editor
                v-model="externalDescription"
                :options="editorOptions"
                class="mb-5"
              />
            </div>
            <div permission="edit.internal-description" v-if="!isTemporaryJob">
              <div class="mb-2 mt-2">Internal Description</div>
              <quill-editor
                v-model="internalDescription"
                :options="editorOptions"
                class="mb-5"
              />
            </div>
            <DatePicker
              permission="edit.deadline-date"
              label="Closing Date"
              validateName="deadlineDate"
              :input-value="deadlineDate"
              @onChange="changeDeadlineDate"
              :showValidationErrors="showValidationErrors"
              required
              isDark
            />
            <DatePicker
              permission="edit.start-date"
              @onChange="val => (startDate = val)"
              label="Proposed Start Date"
              validateName="startDate"
              :input-value="startDate"
              :showValidationErrors="showValidationErrors"
              :key="startDatePickerId"
              :minDate="deadlineDate"
              required
              isDark
              v-if="!isTemporaryJob"
            />
            <DatePicker
              v-else
              permission="edit.start-date"
              @onChange="val => (startDate = val)"
              label="Proposed Start Date"
              :showValidationErrors="showValidationErrors"
              validateName="startDate"
              :input-value="startDate"
              required
              isDark
            />
            <div permission="edit.files">
              <div class="mt-4" permission="files">
                Files
                <FileUpload
                  class="mt-2"
                  @unselectFile="unselectFile"
                  @selectFile="selectFile"
                  :files="files"
                  :fileTypes="['application/pdf']"
                  :limit="2"
                  showPreview
                  customActions
                  disableAutoProceed
                  isMultiple
                  @show="showFile"
                />
              </div>
              <PDFViewFull :data="fileDialog" />
            </div>
          </Permissioned>
        </div>
      </ValidationObserver>
    </template>
    <template v-slot:dialog.action>
      <v-spacer />
      <PrimaryButton :loading="isLoading" @click.native="save">
        Save Changes
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import {
  EDIT_BOOKING,
  FETCH_CURRENT_BOOKING,
  ADD_BOOKING_FILE
} from "@/store/modules/bookings/actions";
import { mapActions, mapGetters } from "vuex";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import Select from "@/components/common/Select";
import {
  GET_LOCATIONS,
  GET_IS_FETCHING_DATA,
  GET_GRADES,
  GET_IS_FETCHING_GRADES,
  GET_WORKING_HOURS,
  GET_IS_FETCHING_WORKING_HOURS,
  GET_CONTRACT_TYPES,
  GET_IS_FETCHING_CONTRACT_TYPES
} from "@/store/modules/data/getters";
import {
  FETCH_LOCATIONS,
  FETCH_GRADES,
  FETCH_WORKING_HOURS,
  FETCH_CONTRACT_TYPES,
  REMOVE_FILE,
  FETCH_TAGS
} from "@/store/modules/data/actions";
import { getFormattedLocation } from "@/utils/locations";
import Permissioned from "@/components/common/Permissioned";
import DatePicker from "@/components/common/DatePicker";
import {
  getAmountInApiFormat,
  getMonetaryDisplayFormat,
  getMonetaryAsNumber
} from "@/utils/helpers";
import { isPermissioned } from "@/utils/permissions";
import { getFilePreviewSettings } from "@/utils/files";
import { DEFAULT_TOOLBAR_OPTIONS } from "@/plugins/v-quill-editor";
import generateRandomId from "uuid/v4";
import { filter, map } from "lodash";
import PDFViewFull from "@/components/common/PDFViewFull";
import FileUpload from "@/components/common/FileUpload";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import Chip from "@/components/common/Chip";

export default {
  name: "EditBasicDetails",
  components: {
    PrimaryButton,
    DialogFull,
    Select,
    Permissioned,
    DatePicker,
    PDFViewFull,
    FileUpload,
    ValidationObserver,
    ValidationProvider,
    Chip
  },
  props: {
    isOpen: Boolean,
    booking: Object
  },
  data() {
    return {
      externalDescription: this.booking.externalDescription,
      internalDescription: this.booking.internalDescription,
      location: this.booking.location,
      startDate: this.booking.startDate,
      deadlineDate: this.booking.deadlineDate,
      salaryBandLower: getMonetaryDisplayFormat(this.booking.salaryBandLower),
      salaryBandUpper: getMonetaryDisplayFormat(this.booking.salaryBandUpper),
      grade: this.booking.grade,
      title: this.booking.title,
      contractType: this.booking.contractType,
      workingHour: this.booking.workingHours,
      complianceRequirements: this.booking.complianceRequirements,
      otherRequirements: this.booking.otherRequirements,
      files: [...this.booking.files],
      isLoading: false,
      editorOptions: {
        modules: {
          toolbar: {
            container: DEFAULT_TOOLBAR_OPTIONS
          }
        }
      },
      startDatePickerId: generateRandomId(),
      fileDialog: { dialog: false },
      moneyConfig: { precision: 2 },
      filesToBeRemoved: [],
      complianceRequirementsOptions: [],
      isFetchingComplianceRequirements: false,
      otherRequirementsOptions: [],
      isFetchingOtherRequirements: false,
      showValidationErrors: false
    };
  },
  created() {
    const filter = { "not-primary": true };
    this.fetchLocations({ filter });
    this.fetchGrades();
    this.fetchWorkingHours();
    this.fetchContractTypes();
    this.fetchComplianceRequirements();
    this.fetchOtherRequirements();
  },
  computed: {
    dialogSettings() {
      return {
        title: "Edit Basic Details",
        dialog: this.isOpen
      };
    },
    ...mapGetters("data", {
      locations: GET_LOCATIONS,
      isFetchingData: GET_IS_FETCHING_DATA,
      grades: GET_GRADES,
      isFetchingGrades: GET_IS_FETCHING_GRADES,
      workingHours: GET_WORKING_HOURS,
      isFetchingWorkingHours: GET_IS_FETCHING_WORKING_HOURS,
      contractTypes: GET_CONTRACT_TYPES,
      isFetchingContractTypes: GET_IS_FETCHING_CONTRACT_TYPES
    }),
    formattedLocations() {
      return this.locations.map(location => {
        delete location.links;
        return {
          label: getFormattedLocation(location),
          value: location
        };
      });
    },
    editSalaryBandLower() {
      return isPermissioned(
        `bookings[${this.booking.id}].edit.salary-band-lower`
      );
    },
    editSalaryBandUpper() {
      return isPermissioned(
        `bookings[${this.booking.id}].edit.salary-band-upper`
      );
    },
    editComplianceRequirements() {
      return isPermissioned(
        `bookings[${this.booking.id}].edit.compliance-requirements`
      );
    },
    editOtherRequirements() {
      return isPermissioned(
        `bookings[${this.booking.id}].edit.other-requirements`
      );
    },
    editSalary() {
      return this.editSalaryBandLower || this.editSalaryBandUpper;
    },
    isTemporaryJob() {
      return this.booking.contractType === "temporary";
    }
  },
  inject: ["currentBookingId"],
  methods: {
    ...mapActions("bookings", {
      editBooking: EDIT_BOOKING,
      fetchCurrentBooking: FETCH_CURRENT_BOOKING,
      addBookingFile: ADD_BOOKING_FILE
    }),
    ...mapActions("data", {
      fetchLocations: FETCH_LOCATIONS,
      fetchGrades: FETCH_GRADES,
      fetchWorkingHours: FETCH_WORKING_HOURS,
      fetchContractTypes: FETCH_CONTRACT_TYPES,
      removeFile: REMOVE_FILE,
      fetchTags: FETCH_TAGS
    }),
    getErrors(errors) {
      return this.showValidationErrors ? errors : [];
    },
    changeDeadlineDate(val) {
      this.deadlineDate = val;
      this.startDate = null;
      this.startDatePickerId = generateRandomId();
    },
    async save() {
      this.showValidationErrors = true;
      const isFormValid = await this.$refs.basicDetailsForm.validate();
      if (isFormValid) {
        this.isLoading = true;
        await this.removeFiles();
        await this.uploadNewFiles();
        await this.editBooking({
          bookingId: this.currentBookingId,
          editedBooking: {
            externalDescription: this.externalDescription,
            internalDescription: this.internalDescription,
            ...(this.editComplianceRequirements && {
              complianceRequirements: this.complianceRequirements
            }),
            ...(this.editOtherRequirements && {
              otherRequirements: this.otherRequirements
            }),
            location: this.location,
            startDate: this.startDate,
            deadlineDate: this.deadlineDate,
            salaryBandLower: getAmountInApiFormat(
              getMonetaryAsNumber(this.salaryBandLower)
            ),
            salaryBandUpper: getAmountInApiFormat(
              getMonetaryAsNumber(this.salaryBandUpper)
            ),
            contractType: this.contractType,
            workingHours: this.workingHour,
            grade: this.grade,
            title: this.title
          }
        });
        this.showValidationErrors = false;
        this.isLoading = false;
        this.close();
      }
    },
    getNewFiles() {
      // return only new uploaded files, already uploaded files have url param
      return filter(this.files, ({ url }) => !url);
    },
    async uploadNewFiles() {
      const newFiles = this.getNewFiles();
      const requests = map(newFiles, file => {
        const formData = new FormData();
        formData.append("file", file.data);
        return this.addBookingFile({
          id: this.currentBookingId,
          data: formData
        });
      });
      return Promise.all(requests);
    },
    async showFile(file) {
      const filePreviewSettings = await getFilePreviewSettings(file);
      this.fileDialog = {
        ...filePreviewSettings,
        dialog: true
      };
    },
    selectFile({ uppyInstance, file }) {
      this.uppyInstance = uppyInstance;
      this.files.push(file);
    },
    async unselectFile({ uppyInstance, file: fileToBeRemoved }) {
      if (fileToBeRemoved.url) {
        this.filesToBeRemoved.push(fileToBeRemoved);
      }
      this.files = filter(this.files, file => file.id !== fileToBeRemoved.id);
      uppyInstance.reset();
    },
    async removeFiles() {
      const requests = map(this.filesToBeRemoved, ({ url }) =>
        this.removeFile(url)
      );
      return Promise.all(requests);
    },
    async fetchComplianceRequirements() {
      this.isFetchingComplianceRequirements = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "compliance_requirements"
        }
      });
      this.complianceRequirementsOptions = map(tags, "term.name");
      this.isFetchingComplianceRequirements = false;
    },
    async fetchOtherRequirements() {
      this.isFetchingOtherRequirements = true;
      const { data: tags } = await this.fetchTags({
        filter: {
          taxonomy: "other_requirements"
        }
      });
      this.otherRequirementsOptions = map(tags, "term.name");
      this.isFetchingOtherRequirements = false;
    },
    close() {
      this.fetchCurrentBooking(this.booking.id);
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss">
.edit-basic-details-content {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
}

.row--form > * {
  padding: 10px 15px 0px 15px;
}
</style>
